import { useRef, useState, type FormEvent } from 'react';
import { PASSWORD_MAX_LENGTH } from '@grc/shared';
import { ApiError } from '../../api';
import { Field } from '../../components/Field';
import { ErrorLine, NoteLine } from '../../components/Lines';
import { errorText } from '../../errors';
import { t, type Code } from '../../i18n';
import { useSession, type SignedOutNotice } from '../../session';

const NOTES: Record<SignedOutNotice, Code> = {
  loggedOut: 'login.loggedOut',
  disabled: 'error.disabled',
};

/** At the login, a username no account can have (`invalid`) is just a wrong username. */
function loginErrorText(err: unknown): string {
  if (err instanceof ApiError && (err.code === 'invalid' || err.code === 'invalid_credentials')) {
    return t('error.credentials');
  }
  return errorText(err);
}

/**
 * The login (D5, D21): the username and the password the club gave. Nobody signs up. The guard
 * (Gate.tsx) moves the member on once the session has them: to the forced change, or to where
 * they were going.
 */
export function Login() {
  const { state, login } = useSession();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string>();
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Why the member is here, until they try: their session ended, or their account was disabled.
  const notice = !tried && state.status === 'signedOut' ? state.notice : undefined;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    // The browser's own bubbles are off (noValidate: their words aren't ours, D6); an empty
    // field just takes the focus.
    if (username.trim() === '') {
      usernameRef.current?.focus();
      return;
    }
    if (password === '') {
      passwordRef.current?.focus();
      return;
    }
    setTried(true);
    setBusy(true);
    setError(undefined);
    try {
      await login(username, password);
    } catch (err) {
      setError(loginErrorText(err));
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <h1>{t('app.name')}</h1>
      <p className="muted">{t('login.intro')}</p>
      <form className="form" onSubmit={onSubmit} noValidate>
        <Field
          ref={usernameRef}
          label={t('login.username')}
          name="username"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-required="true"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <Field
          ref={passwordRef}
          label={t('login.password')}
          name="password"
          type="password"
          autoComplete="current-password"
          maxLength={PASSWORD_MAX_LENGTH}
          aria-required="true"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <ErrorLine text={error} />
        <NoteLine text={notice === undefined ? undefined : t(NOTES[notice])} />
        <button type="submit" className="button" disabled={busy}>
          {busy ? t('login.submitting') : t('login.submit')}
        </button>
      </form>
      <p className="muted small">{t('login.noAccount')}</p>
    </main>
  );
}
